import React, { useRef, useState } from 'react';
import { X, CheckCircle2, UserPlus, MapPin, FlaskConical, CheckSquare, PhoneCall, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { localToday } from '@/shared/utils/utils';

interface QuickActionModalsProps {
  modalType: 'lead' | 'visit' | 'sample' | 'task' | 'followup' | null;
  onClose: () => void;
  onSubmitSuccess?: (message: string) => void;
}

export const QuickActionModals: React.FC<QuickActionModalsProps> = ({
  modalType,
  onClose,
  onSubmitSuccess,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // For follow-ups fetch each place a sales rep can work from: a lead/deal or
  // an actual commercial document.  The selected document becomes the parent,
  // so its own timeline retains the complete client conversation.
  const recordsQuery = useQuery({
    queryKey: ['quick-followup-records'],
    queryFn: async () => {
      const [dealsRes, leadsRes, quotationRes, proformaRes, invoiceRes] = await Promise.allSettled([
        api.get('/api/deals?limit=50'),
        api.get('/api/pipeline?side=lead&limit=50'),
        api.get('/api/sales-ops/orders?documentType=quotation&limit=50&compact=true&sort=date_desc'),
        api.get('/api/sales-ops/orders?documentType=proforma&limit=50&compact=true&sort=date_desc'),
        api.get('/api/sales-ops/orders?documentType=direct_sale&limit=50&compact=true&sort=date_desc'),
      ]);
      const deals = dealsRes.status === 'fulfilled' ? (dealsRes.value.data?.deals ?? []) : [];
      const leads = leadsRes.status === 'fulfilled' ? (leadsRes.value.data?.items ?? []) : [];
      const documents = [
        { response: quotationRes, type: 'quotation' as const, label: 'Quotation' },
        { response: proformaRes, type: 'proforma' as const, label: 'Proforma' },
        { response: invoiceRes, type: 'invoice' as const, label: 'Invoice' },
      ];
      const options: { id: string; type: 'lead' | 'deal' | 'quotation' | 'proforma' | 'invoice'; label: string }[] = [];
      for (const d of deals) {
        options.push({
          id: String(d._id || d.id),
          type: 'deal',
          label: `${d.business_name || d.title || d.name || 'Deal'} — [Deal]`,
        });
      }
      for (const l of leads) {
        options.push({
          id: String(l._id || l.id),
          type: 'lead',
          label: `${l.business_name || l.name || 'Lead'} [Lead]`,
        });
      }
      for (const document of documents) {
        const rows = document.response.status === 'fulfilled'
          ? (document.response.value.data?.data ?? document.response.value.data?.items ?? []) : [];
        for (const row of rows) {
          options.push({
            id: String(row._id || row.id),
            type: document.type,
            label: `${row.customerName || row.customer?.name || 'Client'} — ${row.orderNumber || document.label} [${document.label}]`,
          });
        }
      }
      return options;
    },
    enabled: modalType === 'followup',
  });

  if (!modalType) return null;

  // ── Visit / Sample redirect to full-form pages ────────────────────────────
  // POST /api/visits requires contactId and GPS/purpose setup available on /visits.
  // POST /api/sampling requires line items and catalog selection on /sampling/new.
  if (modalType === 'visit') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs animate-in fade-in duration-150">
        <div className="relative w-full max-w-md rounded-card bg-card p-6 shadow-2xl border border-border text-center">
          <div className="flex items-center justify-between border-b border-outline pb-4 mb-5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-container text-primary">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="text-left">
                <h3 className="text-headline-sm font-semibold text-on-surface">Log Client Field Visit</h3>
                <p className="text-data-xs text-on-surface-subtle">RBM Oil Corporation · Pune Territory</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1 text-on-surface-subtle hover:bg-surface-container hover:text-on-surface-variant transition">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-body-md text-on-surface-variant mb-5">
            Logging a visit requires selecting a verified contact from your directory. Open the Visit Planner to access full GPS tracking, live check-in and visit outcome reporting.
          </p>
          <div className="flex gap-2 justify-center">
            <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onClose(); navigate(ROUTES.visits); }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim transition cursor-pointer"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open Visit Planner
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (modalType === 'sample') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs animate-in fade-in duration-150">
        <div className="relative w-full max-w-md rounded-card bg-card p-6 shadow-2xl border border-border text-center">
          <div className="flex items-center justify-between border-b border-outline pb-4 mb-5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-container text-primary">
                <FlaskConical className="h-4 w-4" />
              </div>
              <div className="text-left">
                <h3 className="text-headline-sm font-semibold text-on-surface">Dispatch Product Sample</h3>
                <p className="text-data-xs text-on-surface-subtle">RBM Oil Corporation · Sales Operations</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1 text-on-surface-subtle hover:bg-surface-container hover:text-on-surface-variant transition">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-body-md text-on-surface-variant mb-5">
            Product samples are linked with catalog batch specifications and dispatch tracking. Open the Sampling form to configure oils and quantities.
          </p>
          <div className="flex gap-2 justify-center">
            <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onClose(); navigate(ROUTES.samplingNew); }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim transition cursor-pointer"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open Sampling Form
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Lead / Task / Followup — real API submit ──────────────────────────────
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const fd = new FormData(e.currentTarget);
    const get = (name: string) => (fd.get(name) as string | null) ?? '';

    try {
      if (modalType === 'lead') {
        // POST /api/leads — real endpoint
        await api.post('/api/leads', {
          name: get('company') || get('name'),
          phone: get('phone'),
          source: get('source') || 'field_visit',
          address: get('product_requirement'),
        });
        queryClient.invalidateQueries({ queryKey: ['pipeline'] });
        queryClient.invalidateQueries({ queryKey: ['deals'] });
        queryClient.invalidateQueries({ queryKey: ['my-stats'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-kpi'] });
      } else if (modalType === 'task') {
        // POST /api/tasks — real endpoint
        await api.post('/api/tasks', {
          title: get('title'),
          client_name: get('client_name'),
          priority: get('priority') || 'medium',
          due_date: get('due_date') || null,
        });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-kpi'] });
      } else if (modalType === 'followup') {
        const recordId = get('record_id');
        if (!recordId) {
          throw new Error('Please select a client or lead to attach the follow-up to.');
        }
        const selected = recordsQuery.data?.find((r) => r.id === recordId);
        const parentType = selected?.type || 'lead';
        const dateStr = get('followup_date') || localToday();
        const timeStr = get('followup_time') || '10:00';
        const followupDate = new Date(`${dateStr}T${timeStr}:00`).toISOString();
        const followupNote = get('followup_note');

        if (!followupNote) {
          throw new Error('Please enter follow-up notes or agenda.');
        }

        // POST /api/followups — real endpoint. Item 44: the same capture set the
        // Follow-up panel sends, and an unchosen contact method stays unrecorded
        // instead of being invented as a phone call.
        await api.post('/api/followups', {
          parentType,
          parentId: recordId,
          followupDate,
          followupNote,
          details: get('followup_details'),
          contactMethod: get('contact_method'),
          customerResponse: get('customer_response'),
          requirement: get('requirement'),
          nextAction: get('next_action'),
          followupCategory: get('followup_category') || 'other',
          customCategory: get('custom_category'),
        });
        queryClient.invalidateQueries({ queryKey: ['followups-overdue'] });
        queryClient.invalidateQueries({ queryKey: ['followups-due-today'] });
        queryClient.invalidateQueries({ queryKey: ['followups-upcoming'] });
        queryClient.invalidateQueries({ queryKey: ['followups'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-kpi'] });
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        onSubmitSuccess?.('Saved successfully');
      }, 700);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Could not save. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (modalType) {
      case 'lead':     return { title: 'Add New Lead / Enquiry',      icon: UserPlus };
      case 'task':     return { title: 'Create Daily Task',            icon: CheckSquare };
      case 'followup': return { title: 'Schedule Follow-up / Call',    icon: PhoneCall };
      default:         return { title: 'Action',                       icon: CheckSquare };
    }
  };

  const info = getTitle();
  const Icon = info.icon;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDateStr = tomorrow.toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="relative w-full max-w-lg rounded-card bg-card p-6 shadow-2xl animate-in zoom-in-95 duration-150 border border-border">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline pb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-container text-primary">
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-headline-sm font-semibold text-on-surface">{info.title}</h3>
              <p className="text-data-xs text-on-surface-subtle">RBM Oil Corporation · Pune Territory</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-on-surface-subtle hover:bg-surface-container hover:text-on-surface-variant transition">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mt-3 rounded-lg bg-destructive-subtle border border-danger/30 px-3 py-2 text-body-sm text-danger-text">
            {error}
          </div>
        )}

        {/* Form Body */}
        <form ref={formRef} onSubmit={handleSubmit} className="mt-4 space-y-4 text-body-sm">
          {modalType === 'lead' && (
            <>
              <div>
                <label className="block font-semibold text-on-surface mb-1">Contact Name *</label>
                <input
                  name="name"
                  required
                  placeholder="e.g. Ramesh Kadam"
                  className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Company / Workshop *</label>
                  <input
                    name="company"
                    required
                    placeholder="e.g. Kadam Auto Lubes"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Mobile / WhatsApp *</label>
                  <input
                    name="phone"
                    required
                    placeholder="10-digit mobile number"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Source</label>
                  <select name="source" className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none">
                    <option value="field_visit">Field Visit</option>
                    <option value="referral">Referral</option>
                    <option value="website">Website</option>
                    <option value="cold_call">Cold Call</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Product Requirement</label>
                  <input
                    name="product_requirement"
                    placeholder="e.g. 4T 20W40, Hydraulic 68"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {modalType === 'task' && (
            <>
              <div>
                <label className="block font-semibold text-on-surface mb-1">Task Title *</label>
                <input
                  name="title"
                  required
                  placeholder="e.g. Follow up on revised GST invoice with accounting"
                  className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Related Customer</label>
                  <input
                    name="client_name"
                    placeholder="e.g. Om Sai Traders"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Priority</label>
                  <select name="priority" className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-semibold text-on-surface mb-1">Due Date</label>
                <input
                  name="due_date"
                  type="date"
                  defaultValue={defaultDateStr}
                  className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                />
              </div>
            </>
          )}

          {modalType === 'followup' && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-semibold text-on-surface">Linked Client / Document *</label>
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(ROUTES.pipeline); }}
                    className="text-data-xs text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <span>View in Pipeline</span>
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
                <select
                  name="record_id"
                  required
                  className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                >
                  <option value="">-- Select Client, Deal, Quotation, Proforma or Invoice --</option>
                  {(recordsQuery.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {recordsQuery.isLoading && (
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Loading your assigned leads...</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Category</label>
                  <select name="followup_category" className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none">
                    <option value="">Select category…</option>
                    <option value="order">Order Follow-up</option>
                    <option value="payment">Payment Follow-up</option>
                    <option value="visit">Visit Follow-up</option>
                    <option value="feedback">Trial Feedback</option>
                    <option value="custom">Custom</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Contact Method</label>
                  <select name="contact_method" className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none">
                    <option value="">Not recorded</option>
                    <option value="call">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                    <option value="visit">Field Visit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Follow-up Date *</label>
                  <input
                    name="followup_date"
                    type="date"
                    required
                    defaultValue={defaultDateStr}
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Time</label>
                  <input
                    name="followup_time"
                    type="time"
                    defaultValue="10:00"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block font-semibold text-on-surface mb-1">Custom Category</label>
                  <input
                    name="custom_category"
                    type="text"
                    maxLength={100}
                    placeholder="Only when Category is Custom"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-on-surface mb-1">Agenda / Discussion Note *</label>
                <textarea
                  name="followup_note"
                  required
                  rows={3}
                  placeholder="e.g. Call client to discuss revised bulk discount and confirm 20W40 order delivery schedule."
                  className="w-full rounded-lg border border-border p-3 text-body-sm focus:border-primary focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Customer response</label>
                  <input
                    name="customer_response"
                    type="text"
                    maxLength={2000}
                    placeholder="What did they say?"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Requirement</label>
                  <input
                    name="requirement"
                    type="text"
                    maxLength={2000}
                    placeholder="What do they need?"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-on-surface mb-1">Next action</label>
                  <input
                    name="next_action"
                    type="text"
                    maxLength={2000}
                    placeholder="What happens next?"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block font-semibold text-on-surface mb-1">Details</label>
                  <input
                    name="followup_details"
                    type="text"
                    maxLength={5000}
                    placeholder="Anything worth keeping on the record"
                    className="w-full rounded-lg border border-border px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2 border-t border-outline pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim transition disabled:opacity-50 cursor-pointer"
            >
              {success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-success-text" />
                  <span>Saved!</span>
                </>
              ) : loading ? (
                <span>Saving…</span>
              ) : (
                <span>Save Action</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
